import * as THREE from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import Experience from '../Experience.js'

const SEGMENT_COUNT = 96

const TEE_COLORS = {
    white: 0xfff4d6,
    yellow: 0xffd400,
    red: 0xff6b5e,
    blue: 0x5aa8ff
}

export default class BallTrajectory {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.terrain = this.experience.world.terrain
        this.holesConfig = this.experience.config.holes
        this.sizes = this.experience.sizes
        this.lineMaterials = new Set()
        this.onResize = () => this.updateLineResolution()
        this.sizes.on('resize.ballTrajectory', this.onResize)

        this.holeKeys = Object.keys(this.holesConfig)
            .map(Number)
            .sort((a, b) => a - b)

        this.group = new THREE.Group()
        this.scene.add(this.group)

        this.arcs = []
        this.teesByHole = new Map()
        this.tempPoint = new THREE.Vector3()
        this.selectedColor = null

        this.indexTees()
        this.setBall()
    }

    indexTees() {
        for (const feature of this.resources.items.tees.features) {
            const hole = feature.properties.hole
            const tee = feature.properties.tee
            if (hole == null || !tee) continue

            if (!this.teesByHole.has(hole)) {
                this.teesByHole.set(hole, new Map())
            }

            this.teesByHole.get(hole).set(tee, feature)
        }
    }

    getTeeFeature(hole, color) {
        const byColor = this.teesByHole.get(hole)
        if (!byColor) return null

        return byColor.get(color) ?? byColor.get('white') ?? byColor.values().next().value ?? null
    }

    getHoleEnd(hole) {
        const index = this.holeKeys.indexOf(hole)
        const nextHole = this.holeKeys[index + 1]
        if (!nextHole) return this.holesConfig[hole][1]
        return this.holesConfig[nextHole][0]
    }

    geoToWorld(feature) {
        const [geoX, geoY] = feature.geometry.coordinates
        const x = -geoX
        const z = -geoY
        const y = this.terrain.getHeightAt(x, z) + 0.05
        return new THREE.Vector3(x, y, z)
    }

    buildSwingCurve(start, end) {
        const distance = start.distanceTo(end)
        const mid = start.clone().lerp(end, 0.5)
        mid.y = Math.max(start.y, end.y) + distance * 0.2

        return new THREE.QuadraticBezierCurve3(start, mid, end)
    }

    // Lignes épaisses en pixels écran (Line2) : nettes à toutes les
    // densités d'écran, là où les lignes WebGL natives restent à 1 px.
    createLineGeometry(curve) {
        const positions = new Float32Array((SEGMENT_COUNT + 1) * 3)

        for (let i = 0; i <= SEGMENT_COUNT; i++) {
            curve.getPoint(i / SEGMENT_COUNT, this.tempPoint)
            positions[i * 3] = this.tempPoint.x
            positions[i * 3 + 1] = this.tempPoint.y
            positions[i * 3 + 2] = this.tempPoint.z
        }

        const geometry = new LineGeometry()
        geometry.setPositions(positions)
        return geometry
    }

    createLineMaterial(color, opacity, { linewidth = 2, dashed = false } = {}) {
        const material = new LineMaterial({
            color,
            transparent: true,
            opacity,
            linewidth,
            dashed,
            dashScale: 1,
            dashSize: 0.012,
            gapSize: 0.01,
            depthWrite: false,
            depthTest: false,
            worldUnits: false
        })
        material.resolution.set(this.sizes.width, this.sizes.height)
        this.lineMaterials.add(material)
        return material
    }

    updateLineResolution() {
        for (const material of this.lineMaterials) {
            material.resolution.set(this.sizes.width, this.sizes.height)
        }
    }

    // Le tracé actif réutilise la géométrie complète : on limite seulement
    // le nombre de segments dessinés (aucune allocation par frame).
    updatePartialGeometry(geometry, t) {
        geometry.instanceCount = Math.max(1, Math.round(SEGMENT_COUNT * t))
    }

    clearArcs() {
        for (const arc of this.arcs) {
            arc.ghostLine.geometry.dispose()
            arc.ghostMaterial.dispose()
            arc.activeGeometry.dispose()
            arc.activeMaterial.dispose()
            this.lineMaterials.delete(arc.ghostMaterial)
            this.lineMaterials.delete(arc.activeMaterial)
            this.group.remove(arc.ghostLine, arc.activeLine)
        }

        this.arcs = []
    }

    buildArcs(color = this.selectedColor) {
        if (!color) return

        const holes = this.resources.items.holes
        const holeByNumber = new Map()

        for (const feature of holes.features) {
            holeByNumber.set(feature.properties.hole, feature)
        }

        const activeColor = TEE_COLORS[color] ?? TEE_COLORS.white

        for (const hole of this.holeKeys) {
            const holeFeature = holeByNumber.get(hole)
            const teeFeature = this.getTeeFeature(hole, color)
            if (!holeFeature || !teeFeature) continue

            const start = this.geoToWorld(teeFeature)
            const end = this.geoToWorld(holeFeature)
            const curve = this.buildSwingCurve(start, end)

            const ghostGeometry = this.createLineGeometry(curve)
            const ghostMaterial = this.createLineMaterial(0xd8c08a, 0.55, { linewidth: 1.25, dashed: true })
            const ghostLine = new Line2(ghostGeometry, ghostMaterial)
            ghostLine.computeLineDistances()
            ghostLine.visible = false
            ghostLine.renderOrder = 4

            const activeGeometry = this.createLineGeometry(curve)
            const activeMaterial = this.createLineMaterial(activeColor, 0.95, { linewidth: 2.25 })
            const activeLine = new Line2(activeGeometry, activeMaterial)
            activeLine.visible = false
            activeLine.renderOrder = 5

            this.group.add(ghostLine, activeLine)

            this.arcs.push({
                hole,
                curve,
                ghostLine,
                ghostMaterial,
                activeLine,
                activeGeometry,
                activeMaterial
            })
        }
    }

    setTeeColor(color) {
        if (!color) return

        this.selectedColor = color
        this.clearArcs()
        this.buildArcs()
    }

    resetTeeColor() {
        this.selectedColor = null
        this.clearArcs()
    }

    setBall() {
        const material = new THREE.SpriteMaterial({
            map: this.resources.items.ball,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            fog: false
        })

        this.ball = new THREE.Sprite(material)
        this.ball.visible = false
        this.ball.renderOrder = 7
        this.ball.scale.set(0.015, 0.015, 1)
        this.group.add(this.ball)
    }

    // Plan du parcours : tous les tracés départ -> green affichés en entier.
    // Sans départ choisi, on montre ceux des départs blancs.
    showOverview() {
        this.overview = true
        if (!this.arcs.length) this.buildArcs(this.selectedColor ?? 'white')

        for (const arc of this.arcs) {
            arc.ghostLine.visible = false
            arc.activeLine.visible = true
            arc.activeGeometry.instanceCount = SEGMENT_COUNT
            arc.activeMaterial.opacity = 0.9
        }
        this.ball.visible = false
    }

    hideOverview(progress) {
        if (!this.overview) return
        this.overview = false

        if (!this.selectedColor) this.clearArcs()
        this.update(progress)
    }

    hideAll() {
        for (const arc of this.arcs) {
            arc.ghostLine.visible = false
            arc.activeLine.visible = false
        }
        this.ball.visible = false
    }

    update(progress) {
        if (!this.selectedColor) {
            this.hideAll()
            return
        }

        const holes = this.holesConfig
        const [firstMin] = holes[this.holeKeys[0]]

        let activeArc = null
        let activeT = 0

        if (progress < firstMin) {
            this.hideAll()
            return
        }

        for (const arc of this.arcs) {
            const [min] = holes[arc.hole]
            const end = this.getHoleEnd(arc.hole)

            if (progress < min) {
                arc.ghostLine.visible = false
                arc.activeLine.visible = false
                continue
            }

            if (progress >= end) {
                arc.ghostLine.visible = false
                arc.activeLine.visible = false
                continue
            }

            // Calé sur le plan-séquence du trou : le tracé attend la fin
            // du fondu et du temps mort avant de partir
            const localT = this.experience.timeline?.getHoleShotProgress(arc.hole, progress)
                ?? THREE.MathUtils.clamp((progress - min) / (end - min), 0, 1)

            // Fondu d'entrée / sortie du tracé au lieu d'une apparition sèche
            const fade = THREE.MathUtils.smoothstep(localT, 0, 0.06) * (1 - THREE.MathUtils.smoothstep(localT, 0.94, 1))

            arc.ghostLine.visible = true
            arc.ghostMaterial.opacity = 0.55 * Math.max(fade, 0.35)

            arc.activeLine.visible = localT > 0.001
            this.updatePartialGeometry(arc.activeGeometry, localT)
            arc.activeMaterial.opacity = 0.95 * fade

            activeArc = arc
            activeT = localT
        }

        if (activeArc && activeT > 0.001 && activeT < 1) {
            activeArc.curve.getPoint(activeT, this.tempPoint)
            this.ball.position.copy(this.tempPoint)
            this.ball.visible = true
        } else {
            this.ball.visible = false
        }
    }

    destroy() {
        this.clearArcs()
        this.sizes.off('resize.ballTrajectory')

        this.ball.material.dispose()
        this.group.remove(this.ball)

        this.scene.remove(this.group)
    }
}
